import { getApproversQueryKey } from "@config";
import {
  useGetDocumentApproversQuery,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetUser,
  usePatchApproverStatusMutation,
  usePatchDocumentMutation,
  usePatchDocumentVersionMutation,
  useReplaceApproversMutation,
} from "@hooks";
import {
  BaseUser,
  Document,
  DocumentVersion,
  DocumentVersionApprover,
  DocumentVersionApproverStatus,
} from "@models";
import { Button, CircularProgress } from "@mui/material";
import { FormData, UserApprovalRoles } from "@types";
import { getRecentlyUpdatedElementIds, queryClient } from "@utils";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSnackbar } from "src/components/Snackbar";
import { getRequiredApproverCount } from "src/utils/documentApprovalPolicy";
import ApproversTabContentLoading from "../ApproversTabContentLoading";
import ApprovalPinModal from "../pin/ApprovalPinModal";
import AssignApprovers from "./AssignApprovers";
import DependencyWarning from "./dependencyWarning/DependencyWarning";
import DocMetadata from "./docMetadata/DocMetadata";
import * as helpers from "./helpers";
import RevisionSummary from "./revisionSummary/RevisionSummary";

type ApproversTabFormProps = {
  docId: string;
  selectedDocVersion: DocumentVersion;
  selectedDoc: Document;
  isEditMode?: boolean;
  onApprovalSubmitSettled?: () => void;
  onApprovalSubmitStart?: () => void;
  onBeforeSubmitApproval?: () => Promise<void>;
  setIsEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ApproversTabForm = ({
  docId: _docId,
  selectedDocVersion,
  selectedDoc,
  isEditMode: controlledIsEditMode,
  onApprovalSubmitSettled,
  onApprovalSubmitStart,
  onBeforeSubmitApproval,
  setIsEditMode: controlledSetIsEditMode,
}: ApproversTabFormProps) => {
  const { deviceId = "", orgId = "" } = useParams();
  const { data: user } = useGetUser();
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId);
  const { data: approvers } = useGetDocumentApproversQuery({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
  });
  const { showSnackbar } = useSnackbar();

  const [internalIsEditMode, setInternalIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeMore, setSeeMore] = useState<boolean>(false);
  const [agreement, setAgreement] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>(helpers.initialFormData);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const isEditMode = controlledIsEditMode ?? internalIsEditMode;
  const setIsEditMode = controlledSetIsEditMode ?? setInternalIsEditMode;

  useEffect(() => {
    if (saving || (isEditMode && formData.selectedUsers.length > 0)) {
      return;
    }

    if (approvers && selectedDocVersion) {
      const approverUsers: BaseUser[] = approvers.map(
        (approver: DocumentVersionApprover) => approver.user
      );

      const author = selectedDocVersion.createdByUser || null;

      const selectedUsersWithAuthorFirst = helpers.includeAuthorInApprovers(
        approverUsers,
        author
      );

      setFormData((prev) => ({
        ...prev,
        selectedUsers: selectedUsersWithAuthorFirst,
        selectedAuthor: author || prev.selectedAuthor,
        revisionSummary:
          prev.revisionSummary || selectedDocVersion.revisionSummary,
        documentMetadata: {
          title: prev.documentMetadata.title || selectedDoc.title,
          regulatoryType:
            prev.documentMetadata.regulatoryType || selectedDoc.regulatoryType,
          functionalGroupId:
            prev.documentMetadata.functionalGroupId ||
            selectedDoc.functionalGroupId,
        },
      }));
      if (!isEditMode) {
        setIsEditMode(
          helpers.shouldStartInEditMode(
            selectedDocVersion,
            selectedDoc,
            approvers.length
          )
        );
      }
    }
  }, [
    approvers,
    formData.selectedUsers.length,
    isEditMode,
    saving,
    selectedDoc,
    selectedDocVersion,
    setIsEditMode,
  ]);

  const replaceApproversMutation = useReplaceApproversMutation({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
    deviceId,
  });

  const patchApproverStatus = usePatchApproverStatusMutation({
    orgId,
    documentId: selectedDocVersion.documentId,
    versionId: selectedDocVersion.id,
    deviceId,
  });
  const patchDocumentMutation = usePatchDocumentMutation();
  const patchDocumentVersionMutation = usePatchDocumentVersionMutation();

  if (
    !approvers ||
    !user ||
    !templatesConfig ||
    !orgId ||
    !selectedDocVersion ||
    !user.id
  ) {
    return <ApproversTabContentLoading />;
  }

  const secondToLastVersion =
    helpers.getPreviousDocVersionWithRevision(selectedDoc);

  const recentlyUpdatedElementIds = getRecentlyUpdatedElementIds(
    selectedDocVersion,
    secondToLastVersion
  );

  const userId = user.id;
  const minimumApprovers = getRequiredApproverCount();

  const userRole = helpers.getUserRole(
    approvers,
    userId,
    selectedDocVersion,
    formData
  );

  const showDependencyWarning =
    !agreement &&
    helpers.getCurrentUserApprovalStatus(approvers, userId) !==
      DocumentVersionApproverStatus.APPROVED &&
    userRole !== UserApprovalRoles.NOT_INVOLVED;

  const { buttonText, buttonColor, buttonDisabled, buttonHidden } =
    helpers.getButtonStateForRole(
      userRole,
      formData,
      selectedDocVersion,
      approvers,
      selectedDoc,
      userId,
      isEditMode,
      true,
      minimumApprovers
    );

  const closeApprovalModal = () => setIsApprovalModalOpen(false);

  const submitRevisionSummary = async () => {
    await patchDocumentVersionMutation.mutateAsync({
      orgId,
      docId: selectedDocVersion.documentId,
      docVersionId: selectedDocVersion.id,
      deviceId,
      data: {
        revisionSummary: formData.revisionSummary,
      },
    });
  };

  const submitMetadata = async () => {
    const metadataMutations: Promise<any>[] = [];
    Object.entries(formData.documentMetadata).forEach(([key, value]) => {
      const isMetadataKeyUpdated =
        value && value !== selectedDoc[key as keyof typeof selectedDoc];

      if (isMetadataKeyUpdated) {
        metadataMutations.push(
          patchDocumentMutation.mutateAsync({
            orgId,
            deviceId,
            docId: selectedDoc.id,
            data: {
              [key]: value,
            },
          })
        );
      }
    });
    // Wait for all metadata mutations to complete
    await Promise.all(metadataMutations);
  };

  const updateAuthor = async (selectedAuthor: BaseUser) => {
    await patchDocumentVersionMutation.mutateAsync({
      orgId,
      docId: selectedDocVersion.documentId,
      docVersionId: selectedDocVersion.id,
      deviceId,
      data: {
        createdBy: selectedAuthor.id,
      },
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    onApprovalSubmitStart?.();

    try {
      /** Metadata */
      await submitMetadata();

      /** Revision Summary */
      if (formData.revisionSummary !== selectedDocVersion.revisionSummary) {
        await submitRevisionSummary();
      }

      /** Author */
      const hasAuthorChanged =
        formData.selectedAuthor?.id !== selectedDocVersion.createdBy;

      if (hasAuthorChanged && formData.selectedAuthor) {
        await updateAuthor(formData.selectedAuthor);
      }

      await onBeforeSubmitApproval?.();
      await updateApprovers();
      setIsEditMode(false);
    } catch {
      showSnackbar("Failed to submit approval changes. Please try again.");
    } finally {
      onApprovalSubmitSettled?.();
      setSaving(false);
    }
  };

  const updateApprovers = async () => {
    const newUsers = formData.selectedUsers.filter(
      (s) => !approvers.find((a) => a.user.id === s?.id)
    );
    const removedUsers = approvers.filter(
      (a) => !formData.selectedUsers.find((s) => s?.id === a.user.id)
    );

    if (newUsers.length || removedUsers.length) {
      await replaceApproversMutation.mutateAsync(
        formData.selectedUsers.filter((user): user is BaseUser => user !== null)
      );
      await queryClient.refetchQueries({
        queryKey: getApproversQueryKey(
          selectedDocVersion.documentId,
          selectedDocVersion.id
        ),
      });
    }
  };

  const handleApproveWithPin = async () => {
    setSaving(true);
    try {
      await patchApproverStatus.mutateAsync({
        status: DocumentVersionApproverStatus.APPROVED,
        approverId: userId,
      });
      setIsApprovalModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleUnapprove = async () => {
    setSaving(true);
    try {
      await patchApproverStatus.mutateAsync({
        status: DocumentVersionApproverStatus.AWAITING_SIGNATURE,
        approverId: userId,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const approverUsers = approvers?.map((approver) => approver.user) || [];
    const author = selectedDocVersion.createdByUser || null;

    const selectedUsers = helpers.includeAuthorInApprovers(
      approverUsers,
      author
    );

    setFormData({
      selectedUsers,
      selectedAuthor: author || null,
      revisionSummary: selectedDocVersion.revisionSummary || "",
      documentMetadata: {
        title: selectedDoc.title || "",
        regulatoryType: selectedDoc.regulatoryType || null,
        functionalGroupId: selectedDoc.functionalGroupId || null,
      },
    });
    setIsEditMode(false);
  };

  const dependentDocs =
    documents && templatesConfig
      ? helpers.getDependentDocs(
          recentlyUpdatedElementIds,
          templatesConfig,
          documents,
          user,
          orgId,
          selectedDoc.templateKey!
        )
      : [];

  const isApproved =
    approvers.find((a) => a.user.id === userId)?.approvalStatus ===
    DocumentVersionApproverStatus.APPROVED;

  const handleConfirm = () => {
    if (isEditMode) {
      handleSubmit();
    } else if (isApproved) {
      handleUnapprove();
    } else {
      setIsApprovalModalOpen(true);
    }
  };

  return (
    <>
      <div
        className={`flex flex-col gap-4 mb-4 overflow-auto ${
          seeMore ? "overflow-scroll max-h-[70vh]" : ""
        }`}
      >
        <DocMetadata
          selectedDoc={selectedDoc}
          formData={formData}
          isEditMode={isEditMode}
          setFormData={setFormData}
        />

        <AssignApprovers
          formData={formData}
          setFormData={setFormData}
          isEditMode={isEditMode}
          organizationId={orgId}
          savedApprovers={approvers}
          saving={saving}
          currentUser={user}
        />

        <RevisionSummary
          formData={formData}
          setFormData={setFormData}
          disabled={!isEditMode}
        />

        {showDependencyWarning && (
          <DependencyWarning
            dependentDocs={dependentDocs}
            setAgreement={setAgreement}
            agreement={agreement}
            seeMore={seeMore}
            setSeeMore={setSeeMore}
          />
        )}
      </div>

      <div className={`flex gap-3 w-full`}>
        {isEditMode &&
          helpers.hasFormBeenInitialised(
            selectedDocVersion,
            selectedDoc,
            approvers.length
          ) && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancel}
              size="large"
              className="flex-1 h-11"
            >
              Cancel
            </Button>
          )}
        {!buttonHidden && (
          <Button
            variant="contained"
            color={buttonColor}
            disabled={buttonDisabled || saving}
            onClick={handleConfirm}
            size="large"
            fullWidth={!isEditMode}
            className="flex-1 h-11"
          >
            {saving ? (
              <span className="flex items-center justify-center">
                <CircularProgress
                  size={16}
                  sx={{ color: "inherit", marginRight: "8px" }}
                />
                Saving...
              </span>
            ) : (
              buttonText
            )}
          </Button>
        )}
        <ApprovalPinModal
          isOpen={isApprovalModalOpen}
          onClose={closeApprovalModal}
          onApprove={handleApproveWithPin}
          user={user}
        />
      </div>
    </>
  );
};

export default ApproversTabForm;
